import type { LEXICON_LETTER_TYPE } from '../../lexicon/lexiconUrls.static'
import { LEXICON_LETTERS_LOWER } from '../../lexicon/lexiconUrls.static'
import { lexiconBaseUrls } from '../../lexicon/getLexiconPath'
import { useIsMdOrGreater, useWindowSize } from '../../hooks/useWindowSize'
import { ScreenBreakpoints } from '../../config/ScreenBreakpoints'
import { useLocale } from '@dreamstack/i18n'
import { cn } from '@dreamstack/util/src/cn'
import { motion } from 'framer-motion'
import includes from 'lodash/includes'
import map from 'lodash/map'
import Link from 'next/link'
import type { FunctionComponent } from 'react'
import { useEffect, useState } from 'react'
import type { Slide } from '../AccentroSlider'
import { LexiconSliderComponent } from './LexiconSliderComponent'

export const LEXICON_SLIDER_SCALE_SIZE = 40

const letterTitleBaseClass =
  'text-px-20 leading-px-30 font-semibold ml-px-16 md:text-px-32 md:leading-px-48'

const LetterTitle = ({
  isActive,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { isActive?: boolean }) => (
  <div
    {...rest}
    className={cn(
      letterTitleBaseClass,
      isActive ? 'text-accentroGray-full' : 'text-accentroWhite-full',
      className
    )}
  />
)

interface LexiconSliderProps {
  activeLexiconLetter?: string
  lettersWithContent: string[]
}

export const LexiconSlider: FunctionComponent<
  React.PropsWithChildren<LexiconSliderProps>
> = ({ activeLexiconLetter, lettersWithContent }) => {
  const locale = useLocale()
  const isMd = useIsMdOrGreater()
  const { width } = useWindowSize()
  const isMobile = width && width < ScreenBreakpoints.breakpointSm
  const [minHeight, setMinHeight] = useState(0)

  useEffect(() => {
    isMd ? setMinHeight(180) : isMobile ? setMinHeight(80) : setMinHeight(160)
  }, [isMd, isMobile, width])

  const slides: Slide[] = map(LEXICON_LETTERS_LOWER, (letter) => {
    const baseUrl = locale === 'de' ? lexiconBaseUrls.de : lexiconBaseUrls.en
    if (!includes(lettersWithContent, letter.toUpperCase())) {
      return {
        slide: (
          <div
            className="bg-accentroGray-300 flex items-start"
            style={{
              minWidth: `${minHeight}px`,
              minHeight: `${minHeight}px`,
              maxWidth: `${minHeight}px`,
              maxHeight: `${minHeight}px`,
            }}
          >
            <span className="text-px-20 leading-px-30 text-white font-semibold mt-px-16 ml-px-16 md:text-px-32 md:leading-px-48">
              {letter.toUpperCase()}
            </span>
          </div>
        ),
        active: false,
        hasContent: false,
      }
    } else {
      return {
        slide: (
          <Link
            href={`${baseUrl}/${letter}`}
            as={`${baseUrl}/${letter}`}
            passHref
          >
            <motion.div
              whileHover={{
                backgroundColor: `${
                  activeLexiconLetter === letter.toUpperCase()
                    ? '#fff'
                    : '508090'
                }`,
                minWidth: `${minHeight + LEXICON_SLIDER_SCALE_SIZE}px`,
                minHeight: `${minHeight + LEXICON_SLIDER_SCALE_SIZE}px`,
                maxWidth: `${minHeight + LEXICON_SLIDER_SCALE_SIZE}px`,
                maxHeight: `${minHeight + LEXICON_SLIDER_SCALE_SIZE}px`,
              }}
              style={{
                minWidth: `${minHeight}px`,
                minHeight: `${minHeight}px`,
                maxWidth: `${minHeight}px`,
                maxHeight: `${minHeight}px`,
                background: `${
                  activeLexiconLetter === letter.toUpperCase()
                    ? '#fff'
                    : '508090'
                }`,
              }}
              className="flex items-start"
            >
              <LetterTitle
                isActive={activeLexiconLetter === letter.toUpperCase()}
              >
                {letter.toUpperCase()}
              </LetterTitle>
            </motion.div>
          </Link>
        ),
        active: activeLexiconLetter === letter.toUpperCase(),
        hasContent: true,
      }
    }
  })
  let current = 0

  if (!!activeLexiconLetter) {
    const activeLetterSlide =
      activeLexiconLetter.toLowerCase() as LEXICON_LETTER_TYPE[number]
    current = LEXICON_LETTERS_LOWER.indexOf(activeLetterSlide)
  }

  return (
    <div className="mt-px-40 col-span-full ml:mt-px-80">
      {isMd && (
        <LexiconSliderComponent
          slides={slides}
          minHeight={180}
          currentSlide={current}
        />
      )}
      {isMobile && (
        <LexiconSliderComponent
          slides={slides}
          minHeight={80}
          currentSlide={current}
        />
      )}
      {!isMd && !isMobile && (
        <LexiconSliderComponent
          slides={slides}
          minHeight={160}
          currentSlide={current}
        />
      )}
    </div>
  )
}
